import "./resolve.css";
import type { Printing } from "@webscan/catalogue.js";
import { captureKey } from "../../shared/captures";
import {
  discardSessionFind,
  getSessionFind,
  resolveSessionFind,
  restoreSessionFind,
  sessionReady,
  sessionStorageIssue,
  subscribeSession,
  unresolvedFinds,
  type SessionFind,
} from "../../shared/scan-session";
import { make } from "../dom";
import { formatFindRange, printingLabel } from "../find-prices";
import { ImageSource } from "../image-source";
import { formatPrice, potentialPrice } from "../prices";
import {
  allPrintings,
  cachedPrintings,
  languageLabel,
  localPrintings,
  treatmentLabel,
  type ReviewPrinting,
} from "./catalogue";
import { mountComparison } from "./comparison";
import { printingImageSource } from "./printing-image";

type Mode = "step" | "enlarged" | "printings" | "compare";
type ReviewCandidate = { printing: ReviewPrinting; score: number };

function comparisonKey(printing: Printing): string {
  return `${printing.scryfall || captureKey(printing)}:${printing.face}`;
}

function button(text: string, className = "review-text-button"): HTMLButtonElement {
  const node = make("button", className, text);
  node.type = "button";
  return node;
}

function label(text: string): HTMLElement {
  return make("div", "review-label", text);
}

function time(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function setLabel(printing: Printing): string {
  return [printing.setName || printing.setCode.toUpperCase(), printing.released.slice(0, 4)]
    .filter(Boolean)
    .join(" · ");
}

function priceText(printing: Printing): string {
  const price = potentialPrice(printing);
  return formatPrice(price.cents, price.currency);
}

function priceNode(printing: Printing): HTMLElement {
  const price = potentialPrice(printing);
  const node = make("span", "review-price", formatPrice(price.cents, price.currency));
  if (price.foil) {
    node.append(make("small", "review-price-note", "foil potential"));
    node.title = `Foil potential; finish is not detected${price.plain === null ? "" : ` · ${formatPrice(price.plain, price.currency)} non-foil`}`;
  }
  return node;
}

function candidateRanges(candidates: ReviewCandidate[]): string {
  const prices = candidates.map(({ printing }) => potentialPrice(printing));
  const labels: string[] = [];
  for (const currency of ["USD", "EUR"] as const) {
    const cents = prices
      .filter((value) => value.currency === currency && value.cents !== null)
      .map((value) => value.cents!);
    if (cents.length)
      labels.push(formatFindRange({ currency, low: Math.min(...cents), high: Math.max(...cents) }));
  }
  if (prices.some((price) => price.cents === null) && labels.length) labels.push("some unpriced");
  if (prices.some((price) => price.foil)) labels.push("foil potential");
  return labels.join("\n") || "—";
}

function normalized(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase();
}

/** Review owns a modal surface, but never changes the scanner's interaction state. */
export function mountReview(
  host: HTMLElement,
  workspace: HTMLElement,
): {
  element: HTMLElement;
  open: (atId?: string) => Promise<void>;
  close: () => void;
} {
  const surface = make("section", "review-screen");
  surface.id = "reviewScreen";
  surface.hidden = true;
  surface.tabIndex = -1;
  surface.setAttribute("role", "dialog");
  surface.setAttribute("aria-modal", "true");
  surface.setAttribute("aria-label", "Review uncertain printings");
  host.append(surface);
  let queue: string[] = [];
  let index = 0;
  let mode: Mode = "step";
  let opened = false;
  let generation = 0;
  let previousFocus: HTMLElement | null = null;
  let previousInert = false;
  let toast: { previous: SessionFind; text: string } | null = null;
  let sheet: HTMLElement | null = null;
  let sheetFocus: HTMLElement | null = null;
  const captureSource = new ImageSource();
  const finishImages = new Map<string, { image: HTMLImageElement; source: ImageSource }>();
  let request: AbortController | null = null;
  let requestName = "";
  let catalogueError = false;
  let catalogueLoading = false;
  let query = "";
  let filter: "all" | "english" | "nonfoil" = "all";
  let otherCards = false;
  let notice = "";
  let renderedFindId: string | undefined;
  let comparison: ReturnType<typeof mountComparison> | null = null;
  let comparisonOrigin: { focus: string; scrollTop: number } | null = null;
  const discarded = new Set<string>();

  const current = (): SessionFind | undefined => getSessionFind(queue[index]);
  const scanName = (find: SessionFind): string => find.scannedAs?.printing.name ?? find.name;
  const imageSource = (find: SessionFind): string | null =>
    captureSource.get(find.capture ?? find.preview);
  const clearFinishImages = (): void => {
    for (const { source } of finishImages.values()) source.clear();
    finishImages.clear();
  };
  const updateFinishImage = (find: SessionFind): void => {
    const entry = finishImages.get(find.id);
    if (!entry) return;
    const source = entry.source.get(find.preview ?? find.capture);
    if (source && entry.image.getAttribute("src") !== source) entry.image.src = source;
    else if (!source && entry.image.hasAttribute("src")) entry.image.removeAttribute("src");
    if (entry.image.hidden !== !source) entry.image.hidden = !source;
  };
  const scanImage = (
    find: SessionFind,
    alt: string,
    className = "",
  ): [HTMLImageElement, HTMLElement] => {
    const source = imageSource(find);
    const image = make("img", className);
    image.dataset.reviewScan = "";
    if (source) image.src = source;
    image.hidden = !source;
    image.alt = alt;
    image.draggable = false;
    const unavailable = make("span", "review-image-unavailable", "Capture unavailable");
    unavailable.dataset.reviewScanPlaceholder = "";
    unavailable.hidden = !!source;
    return [image, unavailable];
  };
  const captureNote = (find: SessionFind): string =>
    find.preview
      ? "Full scan unavailable; enlarging the saved preview."
      : "This scan has no saved capture.";
  const updateMedia = (find: SessionFind): void => {
    const source = imageSource(find);
    // Keep the image, crop, gestures and focused controls mounted while the async JPEG
    // replaces a preview. This also upgrades an initially empty image without a render.
    for (const image of surface.querySelectorAll<HTMLImageElement>("[data-review-scan]")) {
      if (source && image.getAttribute("src") !== source) image.src = source;
      else if (!source && image.hasAttribute("src")) image.removeAttribute("src");
      if (image.hidden !== !source) image.hidden = !source;
    }
    for (const placeholder of surface.querySelectorAll<HTMLElement>(
      "[data-review-scan-placeholder]",
    )) {
      if (placeholder.hidden !== !!source) placeholder.hidden = !!source;
    }
    const note = surface.querySelector<HTMLElement>("[data-review-capture-note]");
    if (note) {
      if (note.hidden !== !!find.capture) note.hidden = !!find.capture;
      if (!find.capture && note.textContent !== captureNote(find))
        note.textContent = captureNote(find);
    }
    comparison?.updateMedia(source, !find.capture);
  };
  const availablePrintings = (find: SessionFind): ReviewPrinting[] => {
    const name = scanName(find);
    return cachedPrintings(name) ?? localPrintings(name);
  };
  const candidates = (find: SessionFind, sameName = true): ReviewCandidate[] => {
    const name = scanName(find);
    const all = cachedPrintings(name);
    const seen = new Set<string>();
    return find.candidates
      .filter(({ printing }) => (printing.name === name) === sameName)
      .filter(({ printing }) => {
        const key = printing.scryfall || captureKey(printing);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ printing, score }) => {
        // Candidate prices remain those shown before resolution; remote metadata fills
        // only the missing visible distinctions. All-printings choices use live prices.
        const metadata = all?.find(
          (value) => value.scryfall === printing.scryfall && value.face === printing.face,
        );
        return {
          score,
          printing: metadata
            ? {
                ...metadata,
                ...printing,
                treatment: metadata.treatment,
                language: metadata.language,
                nonfoil: metadata.nonfoil,
                image: metadata.image,
                imageNormal: metadata.imageNormal,
              }
            : printing,
        };
      });
  };

  const clearComparison = (): void => {
    comparison?.destroy();
    comparison = null;
  };
  const closeSheet = (): void => {
    sheet?.remove();
    sheet = null;
    for (const child of surface.children) if (child instanceof HTMLElement) child.inert = false;
    if (sheetFocus?.isConnected) sheetFocus.focus({ preventScroll: true });
    sheetFocus = null;
  };
  const close = (): void => {
    if (!opened) return;
    opened = false;
    generation++;
    request?.abort();
    request = null;
    requestName = "";
    clearComparison();
    comparisonOrigin = null;
    closeSheet();
    surface.hidden = true;
    surface.replaceChildren();
    workspace.inert = previousInert;
    captureSource.clear();
    clearFinishImages();
    if (previousFocus?.isConnected && !previousFocus.closest("[hidden], [inert]"))
      previousFocus.focus({ preventScroll: true });
    else
      workspace
        .querySelector<HTMLElement>("button:not([hidden]):not(:disabled)")
        ?.focus({ preventScroll: true });
  };
  const focusHeader = (): void => {
    surface
      .querySelector<HTMLElement>(".review-header button, .review-done")
      ?.focus({ preventScroll: true });
  };
  const showMode = (next: Mode): void => {
    const returning = mode === "compare" && next === "step" ? comparisonOrigin : null;
    if (next !== "compare") clearComparison();
    mode = next;
    if (next === "printings") {
      query = current() ? scanName(current()!) : "";
      filter = "all";
    }
    closeSheet();
    render();
    if (returning) {
      const list = surface.querySelector<HTMLElement>("[data-scroll='candidates']");
      if (list) list.scrollTop = returning.scrollTop;
      const source = [...surface.querySelectorAll<HTMLElement>("[data-focus]")].find(
        (node) => node.dataset.focus === returning.focus,
      );
      if (source) {
        source.focus({ preventScroll: true });
        return;
      }
    }
    focusHeader();
  };
  const advance = (): void => {
    clearComparison();
    comparisonOrigin = null;
    index++;
    while (index < queue.length && !getSessionFind(queue[index])) index++;
    mode = "step";
    otherCards = false;
    render();
    focusHeader();
  };
  const failed = (error: unknown): void => {
    notice =
      error instanceof Error ? error.message : "The change could not be saved. Please try again.";
    if (opened) render();
  };
  const choose = (printing: Printing): void => {
    const find = current();
    if (!find) return;
    const previous = find;
    closeSheet();
    toast = { previous, text: `${printing.setCode.toUpperCase()} ${printing.collector} chosen` };
    void resolveSessionFind(find.id, printing).catch(failed);
    advance();
  };
  const discard = (): void => {
    const find = current();
    if (!find) return;
    const previous = find;
    closeSheet();
    discarded.add(find.id);
    toast = { previous, text: "scan discarded" };
    void discardSessionFind(find.id).catch(failed);
    advance();
  };
  const undo = (): void => {
    if (!toast) return;
    const previous = toast.previous;
    toast = null;
    discarded.delete(previous.id);
    index = queue.indexOf(previous.id);
    clearComparison();
    comparisonOrigin = null;
    mode = "step";
    void restoreSessionFind(previous).catch(failed);
    render();
    focusHeader();
  };

  const header = (title: string, progress?: number, back = false): HTMLElement => {
    const head = make("header", "review-header");
    const line = make("div", "review-header-line");
    const action = button(back ? "‹" : "×", "review-close");
    action.dataset.focus = "header-action";
    action.setAttribute("aria-label", back ? "Back to candidates" : "Close review");
    action.onclick = back ? () => showMode("step") : close;
    const heading = make("h2", "review-heading", title);
    line.append(action, heading);
    head.append(line);
    if (progress !== undefined) {
      const rail = make("div", "review-progress");
      rail.setAttribute("role", "progressbar");
      rail.setAttribute("aria-label", "Review progress");
      rail.setAttribute("aria-valuemin", "0");
      rail.setAttribute("aria-valuemax", "100");
      rail.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
      const fill = make("div", "review-progress-fill");
      fill.style.width = `${progress * 100}%`;
      rail.append(fill);
      head.append(rail);
    }
    return head;
  };
  const bottom = (): HTMLElement => make("footer", "review-bottom");
  const subtitle = (left: string, right: string): HTMLElement => {
    const line = label(left);
    line.classList.add("review-section-label");
    line.append(make("span", "review-label-detail", right));
    return line;
  };
  const disclosure = (title: string, count: number, run: () => void): HTMLButtonElement => {
    const row = button("", "review-disclosure");
    row.append(
      make("span", "review-disclosure-plus", "+"),
      make("span", "", title),
      make("span", "review-disclosure-count", String(count)),
    );
    row.onclick = run;
    return row;
  };

  const showComparison = (printing: ReviewPrinting): void => {
    const find = current();
    if (!find) return;
    comparisonOrigin = {
      focus: `compare-${comparisonKey(printing)}`,
      scrollTop: surface.querySelector<HTMLElement>("[data-scroll='candidates']")?.scrollTop ?? 0,
    };
    clearComparison();
    comparison = mountComparison({
      scanSource: imageSource(find),
      previewOnly: !find.capture,
      candidates: [...candidates(find), ...candidates(find, false)],
      selectedKey: comparisonKey(printing),
      onChoose: choose,
    });
    showMode("compare");
  };

  const renderComparison = (find: SessionFind): void => {
    surface.append(header("compare printings", undefined, true));
    comparison?.update(imageSource(find), !find.capture, [
      ...candidates(find),
      ...candidates(find, false),
    ]);
    if (comparison) surface.append(comparison.element);
  };

  const candidateRow = (
    candidate: ReviewCandidate,
    leader: boolean,
    differentSet: boolean,
    differentTreatment: boolean,
    other = false,
  ): HTMLElement => {
    const { printing, score } = candidate;
    const row = make("div", `review-candidate${leader ? " review-candidate--leader" : ""}`);
    row.dataset.printing = captureKey(printing);
    const choice = button("", "review-candidate-choice");
    choice.dataset.focus = `candidate-${comparisonKey(printing)}`;
    choice.setAttribute(
      "aria-label",
      `Choose ${printing.name}, ${printingLabel(printing)}, ${treatmentLabel(printing)}, ${priceText(printing)}`,
    );
    const compare = button("", "review-candidate-compare");
    compare.dataset.focus = `compare-${comparisonKey(printing)}`;
    compare.setAttribute(
      "aria-label",
      `Compare your scan with ${printing.name}, ${printingLabel(printing)}`,
    );
    const thumb = make("span", "review-candidate-thumb");
    const source = printingImageSource(printing);
    const unavailable = (): void => {
      thumb.replaceChildren(make("span", "review-candidate-unavailable", "Image unavailable"));
    };
    // Each choice shows its exact catalogue printing, even when candidates share art.
    // The user's photograph belongs only to the scan anchor and magnified capture.
    if (source) {
      const image = make("img");
      image.alt = "";
      image.loading = "lazy";
      image.decoding = "async";
      image.draggable = false;
      image.crossOrigin = "anonymous";
      image.src = source;
      image.onerror = unavailable;
      thumb.append(image);
    } else unavailable();
    compare.append(thumb, make("span", "review-compare-label", "Compare"));
    compare.onclick = () => showComparison(printing);
    const copy = make("span", "review-candidate-copy");
    if (other) copy.append(make("span", "review-other-name", printing.name));
    const number = make(
      "span",
      "review-collector",
      `${printing.setCode.toUpperCase()} ${printing.collector}`,
    );
    const set = make(
      "span",
      `review-set${differentSet ? " review-difference" : ""}`,
      setLabel(printing),
    );
    const detail = make("span", "review-candidate-details");
    const rarity = make("span", "review-rarity", printing.rarity);
    rarity.dataset.rarity = printing.rarity;
    detail.append(
      rarity,
      make("span", differentTreatment ? "review-difference" : "", treatmentLabel(printing)),
    );
    copy.append(number, set, detail, make("span", "review-choice-label", "Choose printing →"));
    const values = make("span", "review-candidate-values");
    values.append(
      make(
        "span",
        `review-score${leader ? " review-score--leader" : ""}`,
        `${Math.round(score * 100)}%`,
      ),
      priceNode(printing),
    );
    choice.append(copy, values);
    choice.onclick = () => choose(printing);
    row.append(compare, choice);
    return row;
  };

  const showNone = (find: SessionFind): void => {
    sheetFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    for (const child of surface.children) if (child instanceof HTMLElement) child.inert = true;
    sheet = make("div", "review-sheet-scrim");
    const box = make("div", "review-sheet-wrap");
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", "None of these");
    const content = make("div", "review-sheet");
    const title = make("div", "review-sheet-title");
    const count = candidates(find).length;
    title.append(
      make("h3", "", count === 3 ? "None of these three" : "None of these"),
      make("p", "", "What is actually wrong with the match?"),
    );
    const option = (name: string, description: string): HTMLButtonElement => {
      const node = button("", "review-sheet-option");
      node.append(make("strong", "", name), make("span", "", description));
      return node;
    };
    const printing = option(
      "It is a different printing",
      `Search all ${availablePrintings(find).length || "available"} printings of this name →`,
    );
    printing.onclick = () => showMode("printings");
    const card = option(
      "It is a different card",
      "Catalogue-wide name search is not available yet. Check other cards in the candidate list.",
    );
    card.disabled = true;
    const remove = option(
      "Discard this scan",
      "A sleeve, a token, a bad frame — remove it from the session.",
    );
    remove.classList.add("review-danger");
    remove.onclick = discard;
    content.append(title, printing, card, remove);
    const cancel = button("Cancel", "review-sheet-cancel");
    cancel.onclick = closeSheet;
    box.append(content, cancel);
    sheet.append(box);
    sheet.onclick = (event) => {
      if (event.target === sheet) closeSheet();
    };
    surface.append(sheet);
    printing.focus({ preventScroll: true });
  };

  const renderStep = (find: SessionFind): void => {
    const matches = candidates(find);
    const others = candidates(find, false);
    surface.append(header(`card ${index + 1} of ${queue.length}`, (index + 1) / queue.length));
    const anchor = make("div", "review-anchor");
    const capture = button("", "review-capture");
    capture.setAttribute("aria-label", "Enlarge your scan to read the collector number");
    capture.dataset.focus = "capture";
    capture.append(...scanImage(find, "Your scan"));
    capture.onclick = () => showMode("enlarged");
    const copy = make("div", "review-anchor-copy");
    const meta = make(
      "div",
      "review-scan-meta",
      `${time(find.at)}\n${candidateRanges(matches.length ? matches : [{ printing: find.printing, score: 0 }])}`,
    );
    copy.append(
      label("your scan"),
      make("div", "review-scan-name", scanName(find)),
      meta,
      make(
        "p",
        "review-capture-hint",
        "tap to enlarge — the collector number sits on the bottom edge",
      ),
    );
    anchor.append(capture, copy);
    const illustrations = new Set(
      matches.map(({ printing }) => printing.illustration).filter(Boolean),
    );
    const sharedArt =
      matches.length > 0 &&
      illustrations.size === 1 &&
      matches.every(({ printing }) => !!printing.illustration);
    surface.append(
      anchor,
      subtitle(
        "which printing",
        `${sharedArt ? "same art · " : ""}${matches.length} candidate${matches.length === 1 ? "" : "s"}`,
      ),
    );
    const list = make("div", "review-list");
    list.dataset.scroll = "candidates";
    const differentSet = new Set(matches.map(({ printing }) => setLabel(printing))).size > 1;
    const differentTreatment =
      new Set(matches.map(({ printing }) => treatmentLabel(printing))).size > 1;
    for (const [i, candidate] of matches.entries())
      list.append(candidateRow(candidate, i === 0, differentSet, differentTreatment));
    if (!matches.length)
      list.append(
        make(
          "p",
          "review-note",
          "This older scan has no saved candidates. Search all printings of its name to choose one.",
        ),
      );
    if (others.length) {
      const toggle = disclosure("other cards", others.length, () => {
        otherCards = !otherCards;
        render();
      });
      toggle.dataset.focus = "other-cards";
      toggle.setAttribute("aria-expanded", String(otherCards));
      list.append(toggle);
      if (otherCards)
        for (const candidate of others)
          list.append(candidateRow(candidate, false, true, true, true));
    }
    const all = disclosure("all printings of this name", availablePrintings(find).length, () =>
      showMode("printings"),
    );
    all.dataset.focus = "all-printings";
    list.append(all);
    surface.append(list);
    const bar = bottom();
    const back = button("← back");
    back.disabled = index === 0;
    back.onclick = () => {
      toast = null;
      do {
        index--;
      } while (index > 0 && !getSessionFind(queue[index]));
      if (!current()) {
        index++;
        return;
      }
      mode = "step";
      otherCards = false;
      render();
      focusHeader();
    };
    const skip = button("skip", "review-text-button review-push");
    skip.onclick = () => {
      toast = null;
      advance();
    };
    const none = button("none of these", "review-text-button review-none review-danger");
    none.dataset.focus = "none";
    none.onclick = () => showNone(find);
    bar.append(back, skip, none);
    surface.append(bar);
  };

  const renderEnlarged = (find: SessionFind): void => {
    surface.append(header(`your scan · ${time(find.at)}`, undefined, true));
    const body = make("div", "review-enlarged-body");
    const centre = make("div", "review-enlarged-centre");
    const capture = make("div", "review-enlarged-capture");
    capture.append(...scanImage(find, "Your scan, enlarged"), make("div", "review-bottom-band"));
    centre.append(capture);
    const cropArea = make("div", "review-crop-area");
    const cropLabel = subtitle("bottom edge", "3× · pinch to zoom further");
    const crop = make("div", "review-crop");
    crop.tabIndex = 0;
    crop.setAttribute("role", "img");
    crop.setAttribute(
      "aria-label",
      "Magnified bottom-left edge. Pinch or use plus and minus to zoom; drag or use arrow keys to pan.",
    );
    const [cropImage, unavailable] = scanImage(
      find,
      "Magnified bottom edge of your scan",
      "review-crop-image",
    );
    crop.append(cropImage, unavailable);
    installZoom(crop, cropImage, cropLabel);
    cropArea.append(cropLabel, crop);
    const note = make("p", "review-note", captureNote(find));
    note.dataset.reviewCaptureNote = "";
    note.hidden = !!find.capture;
    cropArea.append(note);
    const numbers = make("div", "review-numbers");
    for (const { printing } of candidates(find)) {
      const number = button("", "review-number");
      number.append(
        make("span", "", printing.collector),
        make("small", "", treatmentLabel(printing)),
      );
      number.setAttribute(
        "aria-label",
        `Choose ${printingLabel(printing)}, ${treatmentLabel(printing)}`,
      );
      number.onclick = () => choose(printing);
      numbers.append(number);
    }
    body.append(centre, cropArea, subtitle("the number you are looking for", ""), numbers);
    surface.append(body);
    const bar = bottom();
    const back = button("← candidates");
    back.onclick = () => showMode("step");
    bar.append(
      back,
      make("span", "review-bottom-hint review-push", "tapping a number resolves the card"),
    );
    surface.append(bar);
  };

  const renderPrintings = (find: SessionFind): void => {
    surface.append(header("all printings", undefined, true));
    const tools = make("div", "review-printing-tools");
    const field = make("div", "review-search-wrap");
    const search = make("input", "review-search");
    search.type = "search";
    search.value = query;
    search.autocomplete = "off";
    search.placeholder = "Filter by name, set, number, or treatment";
    search.setAttribute("aria-label", `Filter printings of ${scanName(find)}`);
    const count = make("span", "review-search-count");
    count.setAttribute("aria-live", "polite");
    field.append(search, count);
    const filters = make("div", "review-filters");
    filters.setAttribute("role", "group");
    filters.setAttribute("aria-label", "Printing filters");
    const choices = [
      ["all", "all"],
      ["english", "english"],
      ["nonfoil", "non-foil"],
    ] as const;
    const filterButtons = choices.map(([value, text]) => {
      const pill = button(text, "review-filter");
      pill.onclick = () => {
        filter = value;
        update();
      };
      filters.append(pill);
      return pill;
    });
    const status = make("p", "review-catalogue-status");
    status.setAttribute("role", "status");
    const list = make("div", "review-list review-printing-list");
    list.dataset.scroll = "printings";
    const update = (): void => {
      const all = availablePrintings(find);
      const words = normalized(query).trim().split(/\s+/).filter(Boolean);
      const matches = all.filter((printing) => {
        if (filter === "english" && printing.language !== "en") return false;
        if (filter === "nonfoil" && printing.nonfoil !== true) return false;
        const text = normalized(
          `${printing.name} ${printing.setCode} ${printing.setName} ${printing.collector} ${treatmentLabel(printing)} ${languageLabel(printing.language)}`,
        );
        return words.every((word) => text.includes(word));
      });
      const rich = !!cachedPrintings(scanName(find));
      filterButtons.forEach((node, i) => {
        node.setAttribute("aria-pressed", String(choices[i][0] === filter));
        node.disabled = i > 0 && !rich;
        node.title = node.disabled
          ? "Language and finish filters become available when the complete catalogue loads."
          : "";
      });
      count.textContent = String(matches.length);
      status.textContent = catalogueLoading
        ? "Loading every language and special printing…"
        : catalogueError && !rich
          ? "Offline list only. Connect to load other languages and special printings."
          : "";
      status.hidden = !status.textContent;
      list.replaceChildren();
      const groups = new Map<string, ReviewPrinting[]>();
      for (const printing of matches) {
        const key = setLabel(printing);
        const group = groups.get(key) ?? [];
        group.push(printing);
        groups.set(key, group);
      }
      for (const [name, group] of groups) {
        const section = make("section", "review-printing-group");
        section.append(make("h3", "review-set-heading", name));
        group.sort(
          (a, b) =>
            a.collector.localeCompare(b.collector, undefined, { numeric: true }) ||
            (a.language ?? "").localeCompare(b.language ?? ""),
        );
        for (const printing of group) {
          const row = button("", "review-printing-row");
          row.dataset.printing = printing.scryfall || captureKey(printing);
          const description = [treatmentLabel(printing), languageLabel(printing.language)]
            .filter(Boolean)
            .join(" · ");
          row.setAttribute(
            "aria-label",
            `Choose ${printingLabel(printing)}, ${description}, ${priceText(printing)}`,
          );
          row.append(
            make("span", "review-printing-number", printing.collector),
            make("span", "review-printing-treatment", description),
            priceNode(printing),
          );
          row.onclick = () => choose(printing);
          section.append(row);
        }
        list.append(section);
      }
      if (!matches.length)
        list.append(
          make(
            "p",
            "review-note",
            catalogueLoading ? "Looking up printings…" : "No printings match these filters.",
          ),
        );
      if (catalogueError && !rich) {
        const retry = button("Retry catalogue", "review-retry");
        retry.onclick = () => {
          requestName = "";
          loadCatalogue(find);
          render();
        };
        list.append(retry);
      }
    };
    search.oninput = () => {
      query = search.value;
      update();
    };
    tools.append(field, filters, status);
    surface.append(tools, list);
    update();
    const bar = bottom();
    const cancel = button("cancel", "review-text-button review-push");
    cancel.onclick = () => showMode("step");
    bar.append(make("span", "review-bottom-hint", "Tapping a printing resolves the card"), cancel);
    surface.append(bar);
  };

  const renderFinish = (): void => {
    surface.append(header("review complete", 1));
    const resolved = queue
      .map(getSessionFind)
      .filter((find): find is SessionFind => !!find && find.verdict === "RESOLVED");
    const open = queue.map(getSessionFind).filter((find) => find?.verdict === "AMBIGUOUS").length;
    const summary = make("div", "review-finish-summary");
    const counts = make("div", "review-counts");
    for (const [value, name, colour] of [
      [resolved.length, "resolved", "resolved"],
      [open, "still open", "open"],
      [discarded.size, "discarded", "discarded"],
    ] as const) {
      const count = make("div", `review-count review-count--${colour}`);
      count.append(make("strong", "", String(value)), label(name));
      counts.append(count);
    }
    const totals = new Map<"USD" | "EUR", number>();
    let unpriced = 0;
    let foil = false;
    for (const find of resolved) {
      const price = potentialPrice(find.printing);
      if (price.cents === null) unpriced++;
      else totals.set(price.currency, (totals.get(price.currency) ?? 0) + price.cents);
      foil ||= price.foil;
    }
    const value = make("div", "review-resolved-value");
    value.append(
      label("resolved value"),
      make(
        "strong",
        "",
        [...totals].map(([currency, cents]) => formatPrice(cents, currency)).join(" + ") || "—",
      ),
    );
    summary.append(counts, value);
    if (unpriced || foil)
      summary.append(
        make(
          "p",
          "review-value-note",
          [
            unpriced ? `${unpriced} without a price` : "",
            foil ? "includes foil potential; finish is not detected" : "",
          ]
            .filter(Boolean)
            .join(" · "),
        ),
      );
    surface.append(summary, subtitle("chosen by you", ""));
    const list = make("div", "review-list review-chosen-list");
    for (const find of resolved) {
      const row = make("div", "review-chosen-row");
      const thumb = make("div", "review-chosen-thumb");
      // A late thumbnail patches this node without replacing Finish or its focused
      // controls. These URLs are independent of the active review image and find log.
      const image = make("img");
      image.alt = "";
      finishImages.set(find.id, { image, source: new ImageSource() });
      updateFinishImage(find);
      thumb.append(image);
      const copy = make("div", "review-chosen-copy");
      const identity = make("div", "review-chosen-identity", `${printingLabel(find.printing)} · `);
      identity.append(make("span", "review-yours", "yours"));
      copy.append(make("div", "review-chosen-name", find.name), identity);
      row.append(thumb, copy, priceNode(find.printing));
      list.append(row);
    }
    if (!resolved.length)
      list.append(make("p", "review-note", "No printings chosen in this pass."));
    surface.append(list);
    const footer = make("footer", "review-finish-footer");
    const done = button("Done", "review-done");
    done.dataset.focus = "done";
    done.onclick = close;
    footer.append(done, make("p", "", "Open cards stay in the queue for later."));
    surface.append(footer);
  };

  const loadCatalogue = (find: SessionFind): void => {
    const name = scanName(find);
    if (name === requestName) return;
    request?.abort();
    requestName = name;
    catalogueError = false;
    if (cachedPrintings(name)) {
      catalogueLoading = false;
      return;
    }
    const controller = new AbortController();
    request = controller;
    catalogueLoading = true;
    const timeout = window.setTimeout(
      () => controller.abort(new Error("Catalogue request timed out.")),
      20000,
    );
    void allPrintings(name, controller.signal)
      .then(() => {
        if (request !== controller || !opened) return;
        catalogueLoading = false;
        if (mode !== "enlarged") render();
      })
      .catch(() => {
        if (request !== controller || !opened) return;
        catalogueLoading = false;
        catalogueError = true;
        if (mode !== "enlarged") render();
      })
      .finally(() => window.clearTimeout(timeout));
  };
  function render(): void {
    if (!opened || sheet) return;
    const sameFind = current()?.id === renderedFindId;
    const focused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement.dataset.focus
        : undefined;
    const scroll = surface.querySelector<HTMLElement>("[data-scroll]");
    const scrollTop = sameFind ? (scroll?.scrollTop ?? 0) : 0;
    const search = surface.querySelector<HTMLInputElement>(".review-search");
    const searchFocused = !!search && document.activeElement === search;
    const selection = searchFocused ? [search.selectionStart, search.selectionEnd] : null;
    surface.replaceChildren();
    clearFinishImages();
    const find = current();
    if (!find || find.id !== renderedFindId) captureSource.clear();
    renderedFindId = find?.id;
    const finish = !find || index >= queue.length;
    surface.dataset.mode = finish ? "finish" : mode;
    if (finish) {
      clearComparison();
      request?.abort();
      request = null;
      requestName = "";
      renderFinish();
    } else {
      loadCatalogue(find);
      if (mode === "enlarged") renderEnlarged(find);
      else if (mode === "compare") renderComparison(find);
      else if (mode === "printings") renderPrintings(find);
      else renderStep(find);
    }
    const storage = sessionStorageIssue();
    if (notice || storage) {
      const message = make(
        "p",
        "review-notice",
        notice || "Changes are kept in this tab. Browser storage is unavailable.",
      );
      message.setAttribute("role", "status");
      surface.querySelector(".review-header")?.after(message);
    }
    if (toast) {
      const wrap = make("div", "review-toast-wrap");
      const content = make("div", "review-toast");
      const message = make("span", "", toast.text);
      message.setAttribute("role", "status");
      const revert = button("undo", "review-undo");
      revert.dataset.focus = "undo";
      revert.onclick = undo;
      content.append(message, revert);
      wrap.append(content);
      surface.append(wrap);
    }
    const nextScroll = surface.querySelector<HTMLElement>("[data-scroll]");
    if (nextScroll && nextScroll.dataset.scroll === scroll?.dataset.scroll)
      nextScroll.scrollTop = scrollTop;
    if (focused) {
      const target = [...surface.querySelectorAll<HTMLElement>("[data-focus]")].find(
        (node) => node.dataset.focus === focused,
      );
      target?.focus({ preventScroll: true });
    }
    if (searchFocused) {
      const nextSearch = surface.querySelector<HTMLInputElement>(".review-search");
      nextSearch?.focus({ preventScroll: true });
      if (selection) nextSearch?.setSelectionRange(selection[0], selection[1]);
    }
  }

  // The fixed pass is stable while scanning adds more cards in the background.
  subscribeSession((change) => {
    if (!opened || change.type === "add") return;
    if (change.mediaOnly) {
      if (surface.dataset.mode === "finish") {
        for (const id of change.id ? [change.id] : finishImages.keys()) {
          const find = getSessionFind(id);
          if (find) updateFinishImage(find);
        }
        return;
      }
      const find = current();
      if (find && find.id === renderedFindId && (!change.id || change.id === find.id))
        updateMedia(find);
      return;
    }
    if (sheet) return;
    if (change.id && !queue.includes(change.id)) return;
    // Mutations initiated by this UI synchronously update the store; the caller
    // advances the step before this microtask paints the new snapshot.
    queueMicrotask(() => {
      if (!opened || mode === "enlarged") return;
      if (change.id && current() && change.id !== current()?.id) return;
      render();
    });
  });
  surface.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (sheet) closeSheet();
      else if (mode !== "step") showMode("step");
      else close();
    }
    if (event.key !== "Tab") return;
    const root = sheet ?? surface;
    const nodes = [...root.querySelectorAll<HTMLElement>("button, input, [tabindex='0']")].filter(
      (node) => !node.closest("[hidden], [inert]") && !node.matches(":disabled"),
    );
    const first = nodes[0];
    const last = nodes.at(-1);
    if (!first) {
      event.preventDefault();
      surface.focus();
    } else if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === surface)
    ) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  return {
    element: surface,
    close,
    open: async (atId?: string): Promise<void> => {
      const turn = ++generation;
      await sessionReady();
      if (turn !== generation) return;
      if (!opened) {
        previousFocus =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        previousInert = workspace.inert;
      }
      opened = true;
      surface.hidden = false;
      workspace.inert = true;
      queue = unresolvedFinds().map((find) => find.id);
      index = Math.max(0, atId ? queue.indexOf(atId) : 0);
      clearComparison();
      comparisonOrigin = null;
      mode = "step";
      toast = null;
      notice = "";
      otherCards = false;
      discarded.clear();
      render();
      focusHeader();
    },
  };
}

/** The CSS starts at a true 300%; gestures zoom and pan that exact same capture. */
function installZoom(crop: HTMLElement, image: HTMLImageElement, label: HTMLElement): void {
  const pointers = new Map<number, { x: number; y: number }>();
  let scale = 3;
  let x = 0;
  let y = 0;
  let distance = 0;
  let midpoint = { x: 0, y: 0 };
  const paint = (): void => {
    const width = crop.clientWidth;
    const height = (image.naturalHeight / (image.naturalWidth || 1)) * width * scale;
    x = Math.max(width - width * scale, Math.min(width * 0.02, x));
    y = Math.max(-crop.clientHeight * 0.03, Math.min(Math.max(0, height - crop.clientHeight), y));
    image.style.width = `${scale * 100}%`;
    image.style.transform = `translate(${x}px, ${y}px)`;
    const detail = label.querySelector(".review-label-detail");
    if (detail) detail.textContent = `${Number(scale.toFixed(1))}× · pinch to zoom further`;
  };
  const geometry = (): { distance: number; midpoint: { x: number; y: number } } => {
    const [first, second = first] = [...pointers.values()];
    return {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      midpoint: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
    };
  };
  crop.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    crop.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    ({ distance, midpoint } = geometry());
  });
  crop.addEventListener("pointermove", (event) => {
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const next = geometry();
    if (distance > 0 && next.distance > 0)
      scale = Math.max(3, Math.min(12, (scale * next.distance) / distance));
    x += next.midpoint.x - midpoint.x;
    y += next.midpoint.y - midpoint.y;
    ({ distance, midpoint } = next);
    paint();
  });
  const release = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
    if (pointers.size) ({ distance, midpoint } = geometry());
    else distance = 0;
  };
  crop.addEventListener("pointerup", release);
  crop.addEventListener("pointercancel", release);
  crop.addEventListener("lostpointercapture", release);
  crop.addEventListener(
    "wheel",
    (event) => {
      event.preventDefault();
      scale = Math.max(3, Math.min(12, scale * Math.exp(-event.deltaY * 0.002)));
      paint();
    },
    { passive: false },
  );
  crop.addEventListener("keydown", (event) => {
    const actions: Record<string, () => void> = {
      "+": () => {
        scale = Math.min(12, scale + 0.5);
      },
      "=": () => {
        scale = Math.min(12, scale + 0.5);
      },
      "-": () => {
        scale = Math.max(3, scale - 0.5);
      },
      ArrowLeft: () => {
        x += 24;
      },
      ArrowRight: () => {
        x -= 24;
      },
      ArrowUp: () => {
        y += 24;
      },
      ArrowDown: () => {
        y -= 24;
      },
      "0": () => {
        scale = 3;
        x = y = 0;
      },
    };
    if (!actions[event.key]) return;
    event.preventDefault();
    actions[event.key]();
    paint();
  });
}
